import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import * as api from '../api/client'
import type { AudioTrack, ProjectMode, TrackKind } from '../api/types'
import type { MeStatus } from '../audio/preview'
import { formatBytes, formatTime } from '../lib/format'
import { pickFile } from '../lib/media'
import {
  MODE_HINTS,
  MODE_LABELS,
  PROJECT_MODES,
  TRACK_KINDS,
  TRACK_KIND_LABELS,
  TRACK_KIND_SHORT,
  isRenderMode,
  isTrackKind,
  trackTitle,
} from '../lib/tracks'
import { pushToast } from '../lib/toast'
import { Modal } from './Modal'

export type TrackUploadRequest = { file: File; kind: TrackKind; label: string }

export type TracksPanelProps = {
  tracks: AudioTrack[]
  mode: ProjectMode
  /** false — бэкенд ещё не умеет дорожки (404 на /tracks). */
  available: boolean
  unavailableReason: string | null
  loading: boolean
  uploadProgress: number | null
  meStatus: MeStatus
  meError: string | null
  onReload: () => void
  onModeChange: (mode: ProjectMode) => void
  onPatchTrack: (id: string, patch: { kind?: TrackKind; label?: string }) => void
  onDeleteTrack: (id: string) => void
  onUploadTrack: (request: TrackUploadRequest) => void
}

const ME_STATUS_TEXT: Record<MeStatus, string> = {
  absent: 'дорожка M&E не назначена',
  loading: 'декодирую M&E…',
  ready: 'M&E готова к превью',
  error: 'M&E не декодировалась',
}

type TrackRowProps = {
  track: AudioTrack
  isBase: boolean
  disabled: boolean
  onPatch: TracksPanelProps['onPatchTrack']
  onDelete: TracksPanelProps['onDeleteTrack']
}

function TrackRow({ track, isBase, disabled, onPatch, onDelete }: TrackRowProps): JSX.Element {
  const [label, setLabel] = useState(track.label)
  const [listening, setListening] = useState(false)

  useEffect(() => setLabel(track.label), [track.label])

  const commitLabel = (): void => {
    const next = label.trim()
    if (!next || next === track.label) {
      setLabel(track.label)
      return
    }
    onPatch(track.id, { label: next })
  }

  return (
    <div className={`track-row${isBase ? ' base' : ''}`}>
      <div className="track-head">
        <span className={`track-kind-badge kind-${track.kind}`}>{TRACK_KIND_SHORT[track.kind]}</span>
        <input
          type="text"
          value={label}
          disabled={disabled}
          aria-label={`Метка дорожки ${trackTitle(track)}`}
          onChange={(e) => setLabel(e.currentTarget.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
            if (e.key === 'Escape') setLabel(track.label)
          }}
        />
        <span className="mono faint">{formatTime(track.duration)}</span>
      </div>

      <div className="track-controls">
        <select
          value={track.kind}
          disabled={disabled}
          aria-label="Тип дорожки"
          onChange={(e) => {
            const value = e.currentTarget.value
            if (isTrackKind(value)) onPatch(track.id, { kind: value })
          }}
        >
          {TRACK_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {TRACK_KIND_LABELS[kind]}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="ghost small"
          onClick={() => setListening((v) => !v)}
          title="Послушать дорожку отдельно"
        >
          {listening ? 'Скрыть плеер' : '▶ Прослушать'}
        </button>
        <span className="spacer" />
        <span className="faint">
          {track.sourceIndex === null ? 'загружена файлом' : `из контейнера #${track.sourceIndex + 1}`}
        </span>
        <button
          type="button"
          className="ghost small"
          disabled={disabled}
          onClick={() => {
            if (window.confirm(`Удалить дорожку «${trackTitle(track)}»?`)) onDelete(track.id)
          }}
        >
          Удалить
        </button>
      </div>

      {listening ? (
        <audio controls preload="none" src={api.trackUrl(track.id)}>
          Браузер не умеет играть этот формат.
        </audio>
      ) : null}
    </div>
  )
}

export function TracksPanel({
  tracks,
  mode,
  available,
  unavailableReason,
  loading,
  uploadProgress,
  meStatus,
  meError,
  onReload,
  onModeChange,
  onPatchTrack,
  onDeleteTrack,
  onUploadTrack,
}: TracksPanelProps): JSX.Element {
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [uploadKind, setUploadKind] = useState<TrackKind>('me')
  const [uploadLabel, setUploadLabel] = useState('')

  const meTrack = tracks.find((track) => track.kind === 'me') ?? null
  const dialogueTrack = tracks.find((track) => track.kind === 'dialogue') ?? null
  const busy = loading || uploadProgress !== null

  const choose = async (): Promise<void> => {
    const file = await pickFile(api.TRACK_ACCEPT)
    if (!file) return
    if (file.size > api.MAX_TRACK_BYTES) {
      pushToast('error', `Файл ${formatBytes(file.size)} — лимит 512 МБ`)
      return
    }
    setPendingFile(file)
    setUploadLabel(file.name.replace(/\.[^.]+$/, ''))
    setUploadKind(meTrack ? 'dialogue' : 'me')
  }

  return (
    <section className="panel">
      <header>
        <h2>Дорожки и режим</h2>
        {loading ? <span className="badge">загружаю…</span> : null}
        <span className="spacer" />
        <button type="button" className="ghost small" onClick={onReload} disabled={loading}>
          Обновить
        </button>
      </header>

      <div className="stack">
        <div>
          <label htmlFor="project-mode">Режим проекта</label>
          <select
            id="project-mode"
            value={isRenderMode(mode) ? mode : 'mute'}
            disabled={busy}
            onChange={(e) => {
              const value = e.currentTarget.value
              if (isRenderMode(value)) onModeChange(value)
            }}
          >
            {PROJECT_MODES.map((item) => (
              <option key={item} value={item}>
                {MODE_LABELS[item]}
              </option>
            ))}
          </select>
          <p className="faint" style={{ marginTop: 6 }}>
            {MODE_HINTS[mode]}
          </p>
        </div>

        {mode === 'replace' ? (
          meTrack ? (
            <div className="notice ok">
              <strong>Основа микса: {trackTitle(meTrack)}</strong>
              <span className="faint">
                {formatTime(meTrack.duration)} · {ME_STATUS_TEXT[meStatus]}
                {meError ? ` (${meError})` : ''}
              </span>
              <span className="faint">
                Оригинальные реплики в рендер не идут — их место занимают дубли.
              </span>
            </div>
          ) : (
            <div className="notice warn">
              <strong>Дорожки M&E нет — рендер в этом режиме не запустится</strong>
              <span className="faint">
                Сервер ответит «bad_request». Назначьте тип «M&E» какой-нибудь из дорожек ниже или
                загрузите международную дорожку отдельным файлом.
              </span>
            </div>
          )
        ) : null}

        {mode === 'overlay' ? (
          <div className="notice warn">
            <strong>Режим overlay ещё не реализован</strong>
            <span className="faint">
              ML-разделение оригинала запланировано отдельным этапом. Выберите «Глушение
              оригинала» или «Замена на M&E».
            </span>
          </div>
        ) : null}

        <hr />

        {!available ? (
          <div className="notice warn">
            <strong>Дорожки недоступны</strong>
            <span className="faint">
              {unavailableReason ??
                'Сервер не отвечает на /api/projects/{id}/tracks — вероятно, бэкенд ещё не обновлён до этапа 4.'}
            </span>
            <span className="faint">Всё остальное — разметка, запись, режим mute — работает.</span>
          </div>
        ) : tracks.length === 0 ? (
          <p className="muted">
            Дорожек нет. При загрузке видео сервер раскладывает все аудиодорожки контейнера сам;
            если в файле она одна, M&E можно загрузить отдельно.
          </p>
        ) : (
          <div className="track-list">
            {tracks.map((track) => (
              <TrackRow
                key={track.id}
                track={track}
                isBase={mode === 'replace' && track.kind === 'me'}
                disabled={busy}
                onPatch={onPatchTrack}
                onDelete={onDeleteTrack}
              />
            ))}
          </div>
        )}

        {uploadProgress !== null ? (
          <div className="progress">
            <i style={{ width: `${Math.round(uploadProgress * 100)}%` }} />
          </div>
        ) : null}

        <div className="row wrap">
          <button type="button" disabled={!available || busy} onClick={() => void choose()}>
            ⤒ Загрузить дорожку
          </button>
          <span className="faint">
            {dialogueTrack
              ? 'Дорожка реплик есть: доступны guide при записи и автоматическая разметка.'
              : 'Без дорожки «реплики» не будет ни guide в наушниках, ни авторазметки.'}
          </span>
        </div>

        <p className="faint">
          Тип дорожки сервер угадывает по метаданным и часто ошибается — переназначайте руками.
        </p>
      </div>

      {pendingFile ? (
        <Modal
          title="Загрузка дорожки"
          onClose={() => setPendingFile(null)}
          footer={
            <>
              <button type="button" className="ghost" onClick={() => setPendingFile(null)}>
                Отмена
              </button>
              <span className="spacer" />
              <button
                type="button"
                className="primary"
                onClick={() => {
                  onUploadTrack({
                    file: pendingFile,
                    kind: uploadKind,
                    label: uploadLabel.trim() || pendingFile.name,
                  })
                  setPendingFile(null)
                }}
              >
                Загрузить
              </button>
            </>
          }
        >
          <p className="faint mono">
            {pendingFile.name} · {formatBytes(pendingFile.size)}
          </p>
          <div>
            <label htmlFor="track-upload-kind">Тип дорожки</label>
            <select
              id="track-upload-kind"
              value={uploadKind}
              onChange={(e) => {
                const value = e.currentTarget.value
                if (isTrackKind(value)) setUploadKind(value)
              }}
            >
              {TRACK_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {TRACK_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="track-upload-label">Метка</label>
            <input
              id="track-upload-label"
              type="text"
              value={uploadLabel}
              onChange={(e) => setUploadLabel(e.currentTarget.value)}
            />
          </div>
          <p className="faint">
            Принимаются WAV, FLAC, MP3, M4A, OGG, WebM, AAC. Лимит — 512 МБ.
          </p>
        </Modal>
      ) : null}
    </section>
  )
}
